import type React from 'react';

import { cn } from '#/utils/cn';

import type { IconId } from './icons';
import { Icon } from './icons';

export interface ChipProps extends React.AriaAttributes {
  text: string;
  style: 'outlined' | 'dotted';
  state?: 'default' | 'active' | 'disabled';
  size?: 'sm' | 'md';
  prefixIcon?: IconId;
  suffixIcon?: IconId;
  onClick?: () => void;
  onKeyDown?: React.KeyboardEventHandler<HTMLButtonElement>;
  id?: string;
  role?: React.AriaRole;
  tabIndex?: number;
  className?: string;
}

export function Chip({
  text,
  style,
  state = 'default',
  size = 'md',
  prefixIcon,
  suffixIcon,
  onClick,
  onKeyDown,
  id,
  role,
  tabIndex,
  className,
  ...ariaProps
}: ChipProps) {
  const isDisabled = state === 'disabled';

  const classes = cn(
    'focus-visible:outline-chip-accent inline-flex items-center gap-6 rounded-full px-8 py-2 font-medium transition-colors focus-visible:outline-2 focus-visible:outline-offset-2',
    size === 'sm' ? 'text-static-12' : 'text-static-14',
    style === 'dotted' ? 'border-2 border-dashed' : 'border',
    {
      'border-chip-accent text-chip-accent hover:bg-blue-10':
        state === 'default',
      'border-chip-accent text-gray-10 bg-chip-accent': state === 'active',
      'border-chip-accent-disabled text-chip-accent-disabled bg-chip-bg-disabled cursor-not-allowed':
        isDisabled,
    },
    className,
  );

  const iconSize = size === 'sm' ? 12 : 14;

  if (onClick) {
    return (
      <button
        type="button"
        id={id}
        role={role}
        tabIndex={tabIndex}
        className={classes}
        onClick={isDisabled ? undefined : onClick}
        onKeyDown={onKeyDown}
        disabled={isDisabled}
        aria-pressed={role === 'tab' ? undefined : state === 'active'}
        {...ariaProps}
      >
        {prefixIcon && <Icon id={prefixIcon} size={iconSize} />}
        {text}
        {suffixIcon && <Icon id={suffixIcon} size={iconSize} />}
      </button>
    );
  }

  return (
    <span
      id={id}
      role={role}
      className={classes}
      aria-disabled={isDisabled ? true : undefined}
      {...ariaProps}
    >
      {prefixIcon && <Icon id={prefixIcon} size={iconSize} />}
      {text}
      {suffixIcon && <Icon id={suffixIcon} size={iconSize} />}
    </span>
  );
}
